/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { Component } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import * as utils from '../../../utils';
import { ValueHistoryButton } from './modals/value-history-button';
import ValueCalculationButton from './modals/value-calculation-modal';
import { DATE, formatDate } from '../../../utils/date';
import Table, { TableColumn } from '../../table/Table';
import UTRVProofModal from './modals/utrv-proof-modal';
import { UniversalTrackerValueView } from '../../../utils/universalTrackerValueView';
import UniversalTracker from '../../../model/UniversalTracker';
import { UtrvType } from '../../../constants/status';
import { saveUniversalTrackerValue } from '../../../actions/universalTrackerValue';
import { UtrvAssuranceStatus } from '../../../types/universalTrackerValue';
import IconButton from '../../button/IconButton';
import { UtrvPermissions } from '../../../services/permissions/UtrvPermissions';
import { UniversalTrackerModalServiceUtrv } from '../../../reducers/universal-tracker-modal';
import { getStatusIcon } from '../../../utils/universalTrackerValue';
import { CurrentUserData } from '../../../reducers/current-user';
import { ExistingEvidenceFile } from '../../survey/question/questionInterfaces';
import { UserMin } from '../../../constants/users';

const tableColumns: TableColumn[] = [
  {
    accessor: 'utrType',
    Header: 'Data type',
  },
  {
    accessor: 'sortableDate',
    Header: 'Survey end date',
    Cell: (a: { row: { original: { displayDate: string } } }) => a.row.original.displayDate,
  },
  {
    accessor: 'value',
    Header: 'Answer/data value',
    cellProps: {
      className: 'text-right'
    }
  },
  {
    accessor: 'utrStatus',
    Header: 'Status',
    cellProps: {
      className: 'text-center'
    }
  },
  {
    accessor: 'actions',
    Header: 'Actions',
    cellProps: {
      className: 'text-center'
    }
  },
];

interface DataTableProps {
  selectedColumnCode?: string;
  utr: UniversalTracker;
  utrvs: UniversalTrackerModalServiceUtrv[];
  users?: UserMin[];
  documents?: ExistingEvidenceFile[];
  hideProvenance?: boolean;
  reloadUtrvs?: () => void;
  currentUser?: CurrentUserData;
}

class DataTableComponent extends Component<DataTableProps & PropsFromRedux> {

  state = {
    showLedgerInformation: undefined
  }

  showLedgerInformation = (utrv: UniversalTrackerModalServiceUtrv) => {
    this.setState({ showLedgerInformation: utrv });
  }

  hideLedgerInformation = () => {
    this.setState({ showLedgerInformation: undefined });
  }

  getFormattedUTRValue(universalTracker: UniversalTracker, utrv: UniversalTrackerModalServiceUtrv) {
    const { selectedColumnCode } = this.props;

    return <>
      <UniversalTrackerValueView
        utr={universalTracker}
        utrv={utrv}
        valueType={universalTracker.getValueType()}
        showTableDownload={false}
        extractColumnCode={selectedColumnCode}
      />
      {utrv && Boolean(utrv.aggregationCount) ? '*' : ''}
    </>;
  }

  getStatusIcon = (utrValue: UniversalTrackerModalServiceUtrv) => {
    const assuranceStatuses = [UtrvAssuranceStatus.Created, UtrvAssuranceStatus.Completed, UtrvAssuranceStatus.CompletedOpen];
    const iconClass = getStatusIcon(utrValue);

    if (!utrValue.assuranceStatus) {
      return <i className={`fa ${iconClass} text-ThemeSuccessMedium`} />
    }
    if (assuranceStatuses.includes(utrValue.assuranceStatus)) {
      return <i className={`fa ${iconClass} assurance-${utrValue.assuranceStatus}`} />;
    }
    if (
      utrValue.assuranceStatus === UtrvAssuranceStatus.Restated ||
      utrValue.assuranceStatus === UtrvAssuranceStatus.Rejected
    ) {
      return <i className={`fa ${iconClass} text-ThemeSuccessMedium`} />;
    }
  }

  getDataTypeIcon = (utrValue: UniversalTrackerModalServiceUtrv) => {
    const color = utrValue.type === UtrvType.Target ? 'text-ChartTarget'
      : utrValue.type === UtrvType.Baseline ? 'text-ChartBaseline' : 'text-ChartActual';

    return (
      <>
        <i className={`fa fa-circle me-2 ${color}`} />
        <span className='text-ThemeTextLight'>{utils.capitalise(utrValue.type)}</span>
      </>
    )
  }

  render() {
    const { utr, utrvs, hideProvenance } = this.props;

    const valueHistory = [...utrvs].sort((a, b) => {
      return a.effectiveDate > b.effectiveDate ? -1 : b.effectiveDate > a.effectiveDate ? 1 : 0;
    });

    let aggregated = false;
    const columns = hideProvenance ? tableColumns.filter(c => c.accessor !== 'actions') : tableColumns;

    const tableData = valueHistory.map((utrValue) => {
      const formattedValue = this.getFormattedUTRValue(utr, utrValue);
      aggregated = (utrValue.value && Boolean(utrValue.aggregationCount)) || aggregated;
      return {
        displayDate: formatDate(utrValue.effectiveDate, DATE.MONTH_YEAR),
        sortableDate: formatDate(utrValue.effectiveDate, DATE.SORTABLE),
        value: <>{formattedValue}</>,
        utrType: this.getDataTypeIcon(utrValue),
        utrStatus: this.getStatusIcon(utrValue),
        actions: <>
          {this.renderHistoryLedgerBadge(utrValue)}
          {this.renderHistoryButton(utrValue)}
          {this.renderCalculationButton(utrValue)}
          {this.renderRejectButton(utrValue)}
        </>
      }
    });

    const noData = <div className='alert alert-primary mt-3'>There is no history for this tracker</div>;
    return (
      <div className='d-flex w-100 flex-column'>
        <Table className='data-table w-100 mt-2' columns={columns} data={tableData} noData={noData} />
        {aggregated ? <div className='text-right'> * data is aggregated from its children</div> : null}
        {this.state.showLedgerInformation ? <UTRVProofModal utrv={this.state.showLedgerInformation} hide={this.hideLedgerInformation} /> : <></>}
      </div>
    );
  }

  renderHistoryButton(utrValue: UniversalTrackerModalServiceUtrv) {
    const { utr, users, documents } = this.props;

    if (!utrValue._id) {
      return null;
    }
    return (
      <ValueHistoryButton
        utr={utr}
        users={users}
        documents={documents}
        utrValueId={utrValue._id} />
    );
  }

  renderRejectButton(utrv: UniversalTrackerModalServiceUtrv) {
    const { currentUser } = this.props;

    const allowedTypes = [UtrvType.Target, UtrvType.Baseline];
    if (!allowedTypes.includes(utrv.type as UtrvType)) {
      return null;
    }

    if (!currentUser || !UtrvPermissions.canVerify(utrv, undefined, currentUser)) {
      return null;
    }

    const handleReject = async () => {
      await this.props.saveUtrv('reject', utrv._id, {});
      this.props.reloadUtrvs?.();
    }

    return (
      <IconButton
        tooltip='Reject this value'
        color='transparent'
        outline={false}
        onClick={handleReject}
        icon='fal fa-trash-can text-ThemeDangerMedium'
      />
    );
  }

  renderCalculationButton(utrValue: UniversalTrackerModalServiceUtrv) {
    const { utr, currentUser } = this.props;

    if (Boolean(utrValue.aggregationCount) || !utrValue.compositeData || !currentUser?.isStaff) {
      return null;
    }

    return (
      <ValueCalculationButton
        utrCode={utr.getCode()}
        surveyId={utrValue.compositeData.surveyId} />
    );
  }

  renderHistoryLedgerBadge(utrValue: UniversalTrackerModalServiceUtrv) {
    if (!utrValue.ledgerUniversalTrackerValue || !this.props.currentUser?.isStaff) {
      return null;
    }

    const date = formatDate(utrValue.ledgerUniversalTrackerValue.lastUpdatedDate, DATE.LONG);
    const text = <span>This update was stored on the immutable ledger on the {date}</span>;

    return (
      <IconButton
        tooltip={text}
        color='link'
        className='circleButton'
        onClick={() => this.showLedgerInformation(utrValue)}
        icon='fal fa-cubes text-success'
      />
    )
  }
}

const mapDispatchToProps = {
  saveUtrv: saveUniversalTrackerValue,
};

const connector = connect(undefined, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>
export default connector(DataTableComponent);
